import { ROLES, RoleType } from 'types/roles';

import { cleanHostnameForRedirect } from '@utils/helpers/subdomainRedirect';

/**
 * Constructs the appropriate login path based on user role
 * @param role - The user role type
 * @returns Login path string
 * @throws Error if role is not valid
 */
export const getLoginPath = (role: RoleType) => {
  const validRoles = [ROLES.TEACHER, ROLES.SCHOOL, ROLES.ADMIN];
  if (!validRoles.includes(role)) {
    throw new Error(`Invalid role: ${role}. Valid roles are: ${validRoles.join(', ')}`);
  }
  return `/${role}/login`;
};

/**
 * Generates the full login URL with the subdomain based on role.
 * Only executes when the URL is not localhost.
 * @param role - The user role type
 * @returns Full login URL with subdomain, or pathname if localhost
 */
export const getFullLoginUrl = (role: RoleType) => {
  const loginPath = getLoginPath(role);
  const validRoles = [ROLES.TEACHER, ROLES.SCHOOL, ROLES.ADMIN];
  if (!validRoles.includes(role)) {
    throw new Error(`Invalid role: ${role}. Valid roles are: ${validRoles.join(', ')}`);
  }

  // Try to determine the domain and protocol from environment or window location
  let domain: string | undefined;
  let protocol = 'https'; // Default to https for security
  
  // First, try env variable (server-side or exported at build time)
  if (typeof process !== 'undefined' && process.env.NEXT_PUBLIC_APP_URL) {
    const appUrl = process.env.NEXT_PUBLIC_APP_URL;
    const protocolMatch = appUrl.match(/^(https?):\/\//);
    if (protocolMatch && protocolMatch[1]) {
      protocol = protocolMatch[1];
    }
    domain = appUrl.replace(/^https?:\/\//, '');
  } else if (typeof window !== 'undefined') {
    domain = window.location.hostname;
    protocol = window.location.protocol.replace(':', ''); // Remove the colon from 'https:'
  }

  if (!domain) {
    // fallback, just return path if we cannot determine domain
    return loginPath;
  }

  const isLocalhost = domain.includes('localhost') || domain.startsWith('127.') || domain.startsWith('::1');
  if (isLocalhost) {
    return loginPath;
  }

  // Acceptance environment: hosts are acc-app / acc-school / acc-invalkracht / acc-admin.
  // The env prefix is fused into the first segment, so keep the user on the acceptance
  // host and swap only the role part. ROLES.TEACHER === 'invalkracht', so `acc-${role}`
  // yields the correct acc-invalkracht / acc-school / acc-admin host directly.
  const ACC_HOSTS = ['acc-app', 'acc-school', 'acc-invalkracht', 'acc-admin'];
  const domainParts = domain.split('.');
  if (ACC_HOSTS.includes(domainParts[0] ?? '')) {
    const accRootDomain = cleanHostnameForRedirect(domainParts.slice(1).join('.'));
    return `${protocol}://acc-${role}.${accRootDomain}/login`;
  }

  // For admin, use the main domain (no subdomain)
  if (role === ROLES.ADMIN) {
    return `${protocol}://${domain}/admin/login`;
  }

  // For teacher and school, use their role as subdomain if not already included
  // Remove possible subdomain if present in domain
  let rootDomain = domain;
  // If the first subdomain is a role (teacher, school, admin) strip it
  const prefixPattern = new RegExp(`^(${validRoles.join('|')})\\.`);

  // Only test the prefix pattern against the first segment (subdomain) of the rootDomain
  const rootDomainParts = rootDomain.split('.');
  if (
    rootDomainParts.length > 1 &&
    prefixPattern.test(rootDomainParts[0] + '.')
  ) {
    // Remove the matching prefix (subdomain)
    rootDomain = rootDomainParts.slice(1).join('.');
  } else if (rootDomainParts.length > 2 && !rootDomain.includes('brthrs.io')) {
    // Fallback: Strip subdomain for most cases except brthrs.io
    rootDomain = rootDomainParts.slice(-2).join('.');
  }
  rootDomain = cleanHostnameForRedirect(rootDomain);

  return `${protocol}://${role}.${rootDomain}/login`;
};

/**
 * Builds the email confirmation redirect URL for a given role.
 *
 * The confirmation link must land on the role's own subdomain (e.g.
 * acc-school.just1day.nl) so that the middleware resolves the correct role
 * after the PKCE code is exchanged. Pointing it at the role-less app host
 * (NEXT_PUBLIC_APP_URL) made the middleware fall back to the default teacher
 * login. We derive the subdomain origin from getFullLoginUrl (which already
 * encodes the acceptance/production subdomain rules) and fall back to
 * NEXT_PUBLIC_APP_URL on localhost, where getFullLoginUrl returns a relative
 * path and no subdomain exists.
 *
 * @param role - The user role type
 * @returns Absolute URL to /api/auth/confirm with a role-aware next path
 */
export const getEmailConfirmUrl = (role: RoleType) => {
  // Admin cannot self-register (see signUp in auth-actions). Reject here so no
  // caller can produce the broken acc-admin origin + /admin/login path mismatch:
  // getFullLoginUrl puts admin on the bare root domain in some environments and
  // on an acc-admin subdomain in others, while getLoginPath always yields
  // /admin/login, so the two would disagree for admin.
  if (role === ROLES.ADMIN) {
    throw new Error('getEmailConfirmUrl is not supported for the admin role');
  }

  // origin comes from getFullLoginUrl (the role subdomain); path comes from
  // getLoginPath (the real /[locale]/[role]/login route, matching middleware).
  const next = encodeURIComponent(`${getLoginPath(role)}?verified=true`);
  const loginUrl = getFullLoginUrl(role);

  let origin = process.env.NEXT_PUBLIC_APP_URL ?? '';
  try {
    // getFullLoginUrl returns an absolute URL off localhost and a relative
    // path on localhost; new URL() throws on the relative path, so the catch
    // keeps the NEXT_PUBLIC_APP_URL fallback.
    origin = new URL(loginUrl).origin;
  } catch {
    // Keep NEXT_PUBLIC_APP_URL fallback (localhost / undeterminable host).
  }

  return `${origin}/api/auth/confirm?next=${next}`;
};